import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { cx } from "../../../components/ui";
import { api } from "../../../lib/api";
import type { ActionKind } from "./model";

const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";

/** Waypoint's reading of one action, cached for the page. */
const cache = new Map<string, Promise<ActionKind | null>>();
export function readAction(line: string, cls: string, action: string, refresh = false) {
  const k = `${line}:${cls}.${action}`;
  if (refresh || !cache.has(k)) cache.set(k, api<{ action: ActionKind | null }>(`/apps/ja/waypoint/classes/${encodeURIComponent(cls)}/actions/${encodeURIComponent(action)}?line=${line}${refresh ? "&refresh=1" : ""}`).then((r) => r.action).catch(() => null));
  return cache.get(k)!;
}

/**
 * Choose an Infor class and action from Waypoint's index of delivered source (design time only). A tenant extension
 * (FUSD, CH, ZZ) isn't there: type it, and the Designer flags it to be checked on a connection.
 */
export function ActionPicker({ line, cls, action, onPick }: { line: string; cls: string; action: string; onPick: (cls: string, action: string) => void }) {
  const [q, setQ] = useState(cls);
  const [classes, setClasses] = useState<{ cls: string; module: string }[]>([]);
  const [actions, setActions] = useState<{ action: string; kind: string }[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  // An action the class's delivered list doesn't have (a tenant extension on a delivered class, e.g. FUSD), typed in.
  const [other, setOther] = useState(false);
  const [typed, setTyped] = useState("");
  const commitTyped = () => {
    if (typed && typed !== action) onPick(cls, typed);
    if (!typed) setOther(false);
  };

  useEffect(() => {
    setQ(cls);
    setOther(false);
  }, [cls]);
  useEffect(() => {
    if (!open || q.trim().length < 2 || q === cls) return;
    const t = setTimeout(() => {
      api<{ classes: { cls: string; module: string }[] }>(`/apps/ja/waypoint/classes?q=${encodeURIComponent(q.trim())}&line=${line}`)
        .then((r) => setClasses(r.classes))
        .catch((e) => setError((e as Error).message));
    }, 350);
    return () => clearTimeout(t);
  }, [q, open, line, cls]);
  useEffect(() => {
    if (!cls) return setActions(null);
    setBusy(true);
    setError(null);
    api<{ actions: { action: string; kind: string }[] }>(`/apps/ja/waypoint/classes/${encodeURIComponent(cls)}?line=${line}`)
      .then((r) => setActions(r.actions))
      .catch((e) => {
        setActions(null);
        setError((e as Error).message);
      })
      .finally(() => setBusy(false));
  }, [cls, line]);

  return (
    <div className="space-y-2 text-sm">
      <label className="block relative">
        <span className="text-ink-2 font-medium">Class</span>
        <div className="relative">
          <Search className="size-4 absolute left-2.5 top-2.5 text-muted" aria-hidden />
          <input className={cx(input, "pl-8")} value={q} onFocus={() => setOpen(true)} onChange={(e) => setQ(e.target.value.replace(/[^A-Za-z0-9_]/g, ""))} placeholder="Search Infor classes, e.g. PaymentSchedule" />
        </div>
        {open && classes.length > 0 && q !== cls && (
          <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-line bg-surface shadow-lg" role="listbox">
            {classes.map((c) => (
              <li key={c.cls}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-1.5 hover:bg-surface-2"
                  onClick={() => {
                    setOpen(false);
                    setClasses([]);
                    onPick(c.cls, "");
                  }}
                >
                  <span className="font-mono">{c.cls}</span> <span className="text-xs text-muted">{c.module}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {q && q !== cls && (
          <button type="button" className="text-xs text-accent-text hover:underline mt-1" onClick={() => onPick(q, action)}>
            Use “{q}” as typed (a tenant extension)
          </button>
        )}
      </label>
      <label className="block">
        <span className="text-ink-2 font-medium">Action</span>
        {actions && other ? (
          <div className="space-y-1">
            <input className={cx(input, "font-mono")} autoFocus value={typed} onChange={(e) => setTyped(e.target.value.replace(/[^A-Za-z0-9_]/g, ""))} onBlur={commitTyped} onKeyDown={(e) => e.key === "Enter" && commitTyped()} placeholder="e.g. CalculatePaymentsFUSD" aria-label="Action name" />
            <button type="button" className="text-xs text-accent-text hover:underline" onClick={() => setOther(false)}>
              Back to {cls}'s delivered actions
            </button>
          </div>
        ) : actions ? (
          <select
            className={input}
            value={action}
            onChange={(e) => {
              if (e.target.value !== "__other") return onPick(cls, e.target.value);
              setTyped("");
              setOther(true);
            }}
          >
            <option value="">Choose…</option>
            {actions.map((a) => (
              <option key={a.action} value={a.action}>
                {a.action} · {a.kind}
              </option>
            ))}
            {action && !actions.some((a) => a.action === action) && <option value={action}>{action} (not in delivered source)</option>}
            <option value="__other">Other action (a tenant extension)…</option>
          </select>
        ) : (
          <input className={input} value={action} onChange={(e) => onPick(cls, e.target.value.replace(/[^A-Za-z0-9_]/g, ""))} placeholder={busy ? "Reading the class…" : "Action name"} />
        )}
      </label>
      {error && <p className="text-xs text-warn">{error}</p>}
    </div>
  );
}
